import axios from '../common/axios'; // Adjust the path as needed
import { AxiosError } from 'axios';
import { ErrorMessages } from '../components/Excel/constants';

/**
 * Reads the content of a Blob and parses it as JSON.
 * @param {Blob} blob - The Blob to read and parse.
 */
const readBlobAsJson = (blob: Blob): Promise<any> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        // Parse the result as JSON
        const json = JSON.parse(reader.result as string);
        // Resolve the promise with the parsed JSON
        resolve(json);
      } catch (e) {
        // Reject the promise if parsing fails
        reject(new Error(ErrorMessages.FailedToParseJson));
      }
    };
    reader.onerror = () => {
      // Reject the promise if reading the Blob fails
      reject(new Error(ErrorMessages.FailedToReadBlob));
    };
    // Read the Blob as text
    reader.readAsText(blob);
  });
};

/**
 * Creates an Excel file and triggers its download.
 * @param {string} placeCode - The code representing the place (mill).
 * @param {string} tabName - The name of the active tab.
 * @param {number} selectedTime - The selected time for the data.
 */
export const createExcelFile = async (placeCode: string, tabName: string, sourceName: string, selectedTime: number): Promise<void> => {
  try {
    // Get the user's timezone
    const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Call HTTP GET request to create an Excel file
    const response = await axios.get(
      `service/createExcel/${placeCode}?tabName=${tabName}&sourceName=${sourceName}&selectedTime=${selectedTime}&userTimezoneId=${timeZoneId}`,
      {
        //Response is handled as a blob
        responseType: 'blob',
      },
    );

    // Create a URL for the blob and trigger the download
    const url = window.URL.createObjectURL(
      new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    );

    // Create an anchor element to trigger the download
    const link = document.createElement('a');

    // Set the href to the blob URL
    link.href = url;

    // Set the file name
    let fileName = `${placeCode}_${tabName}`;
    if (sourceName?.length > 0) {
      fileName += `_${sourceName}`
    }
    fileName += `_${selectedTime}_Report.xlsx`;

    // Set the download attribute with the file name
    link.setAttribute('download', fileName);

    // Append the link to the document body
    document.body.appendChild(link);

    // Programmatically click the link to trigger the download
    link.click();

    // Remove the link from the document body
    document.body.removeChild(link);
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      // Read the Blob Axios error message as JSON
      const errorJson = await readBlobAsJson(error.response.data);
      // Format the error message
      const errorMessage = `${errorJson.error}${errorJson.details ? `: ${errorJson.details}` : ''}`;
      throw new Error(errorMessage);
    } else {
      // Handle unexpected errors
      throw new Error(ErrorMessages.UnexpectedError);
    }
  }
};
